import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

import DDIOptions from 'src/app/data/ddi';
import Utils, { UtilsValidators } from 'src/app/helpers/utils';
import { FundService } from 'src/app/services/fund/fund.service';

@Component({
  selector: 'app-dashboard-fund-approval-register-transfer',
  templateUrl: './dashboard-fund-approval-register-transfer.component.html',
  styleUrls: ['./dashboard-fund-approval-register-transfer.component.scss'],
})
export class DashboardFundApprovalRegisterTransferComponent
  implements OnInit, OnChanges
{
  @Input() loading = true;

  readonly DDIOptions = DDIOptions;
  readonly form = this.formBuilder.group({
    received: [false],
    transfer_date: [null],
    assembly_date: [null],
    last_company_document: [null],
    last_company_name: [null],
    last_company_contact: [null],
    last_company_email: [null],
    last_company_telephone_ddi: ['55'],
    last_company_telephone_number: [null],
    last_company_cellphone_ddi: ['55'],
    last_company_cellphone_number: [null],
  });

  private readonly receivedControls = [
    'transfer_date',
    'assembly_date',
    'last_company_document',
    'last_company_name',
    'last_company_contact',
    'last_company_email',
    'last_company_telephone_ddi',
    'last_company_telephone_number',
    'last_company_cellphone_ddi',
    'last_company_cellphone_number',
  ];

  constructor(
    private formBuilder: FormBuilder,
    public fundService: FundService
  ) {}

  ngOnInit(): void {
    this.form.controls.received.valueChanges.subscribe(() => {
      this.updateValidators();
      this.publishIntegratedData();
    });
    this.form.valueChanges.subscribe(() => {
      this.publishIntegratedData();
      this.fundService.setFundFormStatus('transfer', true);
    });
    this.updateValidators();
    this.fundService.setFundFormStatus('transfer', true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.loading || changes.loading.currentValue) return;
    this.loadData();
  }

  private loadData(): void {
    const data = this.fundService.data;
    if (!data) return;
    this.form.patchValue(
      {
        received: !!data.transfer_date,
        transfer_date: this.toBrDate(data.transfer_date),
        assembly_date: this.toBrDate(data.assembly_date),
        last_company_document: data.last_company_document,
        last_company_name: data.last_company_name,
        last_company_contact: data.last_company_contact,
        last_company_email: data.last_company_email,
        last_company_telephone_ddi:
          data.last_company_telephone_ddi || '55',
        last_company_telephone_number:
          data.last_company_telephone_number,
        last_company_cellphone_ddi:
          data.last_company_cellphone_ddi || '55',
        last_company_cellphone_number:
          data.last_company_cellphone_number,
      },
      { emitEvent: false }
    );
    this.updateValidators();
    this.publishIntegratedData();
    this.fundService.setFundFormStatus('transfer', true);
  }

  private updateValidators(): void {
    // Recebido é opcional: se ligado, valida formato só quando preenchido.
    const enabled = !!this.form.controls.received.value;

    this.receivedControls.forEach((name) => {
      const control = this.form.get(name);
      if (!control) return;

      if (!enabled) {
        control.clearValidators();
      } else if (name.includes('document')) {
        control.setValidators([Utils.validateWhenNotEmpty(UtilsValidators.cnpj)]);
      } else if (name.includes('email')) {
        control.setValidators([Utils.validateWhenNotEmpty(Validators.email)]);
      } else if (
        name.includes('telephone_number') ||
        name.includes('cellphone_number')
      ) {
        control.setValidators([
          Utils.validateWhenNotEmpty(UtilsValidators.telefone),
        ]);
      } else if (name.includes('date')) {
        const constitutionDate = this.fundService.getConstitutionDate();
        control.setValidators([
          Utils.validateWhenNotEmpty(Utils.validateMinDate),
          Utils.validateWhenNotEmpty(Utils.validateMaxDate),
          ...(constitutionDate
            ? [
                Utils.validateWhenNotEmpty(
                  Utils.validateMinConstitutionDate(constitutionDate)
                ),
              ]
            : []),
        ]);
      } else {
        control.clearValidators();
      }

      control.updateValueAndValidity({ emitEvent: false });
    });

    this.fundService.setFundFormStatus('transfer', true);
  }

  private publishIntegratedData(): void {
    const value = this.form.getRawValue();
    const received = !!value.received;
    this.fundService.setFundFormData({
      ...this.fundService.data,
      transfer: received,
      transfer_date: received ? value.transfer_date : null,
      assembly_date: received ? value.assembly_date : null,
      last_company_document: received ? value.last_company_document : null,
      last_company_name: received ? value.last_company_name : null,
      last_company_contact: received ? value.last_company_contact : null,
      last_company_email: received ? value.last_company_email : null,
      last_company_telephone_ddi: received
        ? value.last_company_telephone_ddi
        : null,
      last_company_telephone_number: received
        ? value.last_company_telephone_number
        : null,
      last_company_cellphone_ddi: received
        ? value.last_company_cellphone_ddi
        : null,
      last_company_cellphone_number: received
        ? value.last_company_cellphone_number
        : null,
    });
  }

  private toBrDate(value: string): string | null {
    if (!value) return null;
    return value.includes('-') ? value.split('-').reverse().join('/') : value;
  }
}
